import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginInput } from '@cmms/validation'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AuthShell } from '../../layouts/AuthShell'
import { APP_NAME } from '../../lib/branding'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { ApiError } from '@cmms/api-client'
import { GoogleSignInButton } from './GoogleSignInButton'

// localStorage key that remembers the last workspace a user logged into
// on this device. Kept around (and updated on each successful login) for
// possible future use (analytics, smart picker default), but no longer
// triggers an auto-redirect — that pattern made "Use a different
// workspace" feel like it didn't work, since users would get bounced
// back to /<slug>/login as soon as they navigated to /login.
const LAST_SLUG_KEY = 'last_workspace_slug'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { slug: urlSlug } = useParams<{ slug?: string }>()
  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  // Workspace picker — set when the email-first endpoint reports that
  // the email exists in more than one workspace. The user clicks a row,
  // we replay the login with that slug and finish.
  const [workspaceOptions, setWorkspaceOptions] = useState<{ slug: string; name: string }[] | null>(null)
  const [pendingCreds, setPendingCreds] = useState<{ email: string; password: string } | null>(null)
  const [finalizing, setFinalizing] = useState(false)

  // Google sign-in — set instead of `pendingCreds` when the picker came
  // from Google rather than the email/password form, so `finalize...`
  // knows which endpoint to resubmit the chosen slug to. `googlePending`
  // holds the "no account found — queued for review" message when a
  // Google account matches no existing user anywhere.
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(null)
  const [googlePending, setGooglePending] = useState<string | null>(null)

  // Workspace-from-URL semantics:
  //   - /bacsys-demo/login → urlSlug = 'bacsys-demo' → hide the field, pre-fill the form
  //   - /login             → urlSlug undefined       → email-first flow (no workspace input)
  const effectiveSlug = (urlSlug ?? '').trim()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { org_slug: effectiveSlug, email: '', password: '' },
  })

  // Keep the form's org_slug in sync if the URL slug ever changes
  // (back/forward navigation, manual edit).
  useEffect(() => {
    setValue('org_slug', effectiveSlug)
  }, [effectiveSlug, setValue])

  // "Use a different workspace" from the slugged URL drops the user
  // back on bare /login. localStorage is also cleared so any saved
  // slug doesn't influence future flows (analytics, etc).
  function useDifferentWorkspace() {
    window.localStorage.removeItem(LAST_SLUG_KEY)
    navigate('/login', { replace: true })
  }

  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  async function onSubmit(values: LoginInput) {
    setSubmitError(null)

    // Slugged URL: workspace is fixed; use the workspace-explicit login.
    if (urlSlug) {
      await runSluggedLogin(urlSlug, values.email, values.password)
      return
    }

    // Bare /login: email-first. Backend figures out the workspace.
    try {
      const result = await api.auth.loginByEmail({
        email: values.email,
        password: values.password,
      })

      // Multi-workspace match — render the picker. We stash the
      // credentials so the picker click can finalize without asking
      // the user to retype.
      if ('needs_workspace' in result) {
        setWorkspaceOptions(result.workspaces)
        setPendingCreds({ email: values.email, password: values.password })
        return
      }

      // Super-admin match — issue token, land on the no-org page.
      if ('type' in result) {
        setTokens(result.access_token, '')
        const user = await api.auth.me()
        setUser(user)
        navigate('/settings?tab=invite-requests', { replace: true })
        return
      }

      // Single workspace match — tokens in hand, log in.
      setTokens(result.access_token, result.refresh_token)
      const user = await api.auth.me()
      setUser(user)
      if (user.org_slug) {
        window.localStorage.setItem(LAST_SLUG_KEY, user.org_slug)
      }
      navigate(from, { replace: true })
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Invalid email or password')
    }
  }

  /**
   * Called from two places: directly when the URL has a slug, and from
   * the workspace picker after the user picks one. Same logic either way.
   */
  async function runSluggedLogin(slug: string, email: string, password: string) {
    setSubmitError(null)
    setFinalizing(true)
    try {
      const tokens = await api.auth.login({ org_slug: slug, email, password })
      setTokens(tokens.access_token, tokens.refresh_token)
      const user = await api.auth.me()
      setUser(user)
      window.localStorage.setItem(LAST_SLUG_KEY, slug)
      navigate(from, { replace: true })
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Invalid email or password')
      setFinalizing(false)
    }
  }

  function backToLoginForm() {
    setWorkspaceOptions(null)
    setPendingCreds(null)
    setGoogleIdToken(null)
    setGooglePending(null)
    setSubmitError(null)
  }

  /**
   * Google Identity Services calls this with the raw ID token once the
   * user picks an account. Mirrors `onSubmit`'s email-first branching
   * (tokens / picker / no match), except a no-match here isn't a 401 —
   * the backend already queued it for admin review.
   */
  async function onGoogleCredential(idToken: string) {
    setSubmitError(null)
    try {
      const result = await api.auth.googleAuth({ id_token: idToken })

      if ('pending' in result) {
        setGooglePending(result.message)
        return
      }

      if ('needs_workspace' in result) {
        setWorkspaceOptions(result.workspaces)
        setGoogleIdToken(idToken)
        setPendingCreds(null)
        return
      }

      setTokens(result.access_token, result.refresh_token)
      const user = await api.auth.me()
      setUser(user)
      if (user.org_slug) {
        window.localStorage.setItem(LAST_SLUG_KEY, user.org_slug)
      }
      navigate(from, { replace: true })
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : "Couldn't sign in with Google")
    }
  }

  /** Second step of the Google picker flow — resubmit the same ID token with the chosen slug. */
  async function finalizeGoogleLogin(slug: string, idToken: string) {
    setSubmitError(null)
    setFinalizing(true)
    try {
      const result = await api.auth.googleAuth({ id_token: idToken, org_slug: slug })
      if (!('access_token' in result) || !('refresh_token' in result)) {
        setSubmitError("Couldn't sign in to that workspace")
        setFinalizing(false)
        return
      }
      setTokens(result.access_token, result.refresh_token)
      const user = await api.auth.me()
      setUser(user)
      window.localStorage.setItem(LAST_SLUG_KEY, slug)
      navigate(from, { replace: true })
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : "Couldn't sign in")
      setFinalizing(false)
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle={`Sign in to continue to ${APP_NAME}.`}
      swap={{ prompt: "Don't have an account?", cta: 'Sign Up', to: '/register-org' }}
    >
      {googlePending ? (
        <GooglePendingNotice message={googlePending} onBack={backToLoginForm} />
      ) : workspaceOptions ? (
        <WorkspacePicker
          options={workspaceOptions}
          onPick={(slug) => {
            if (googleIdToken) {
              finalizeGoogleLogin(slug, googleIdToken)
              return
            }
            if (!pendingCreds) return
            runSluggedLogin(slug, pendingCreds.email, pendingCreds.password)
          }}
          onCancel={backToLoginForm}
          finalizing={finalizing}
          email={pendingCreds?.email ?? ''}
          error={submitError}
        />
      ) : (
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2.5}>
          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}

          {urlSlug ? (
            // Slug came from the URL — show it as static context with
            // an escape hatch instead of an editable field. Keeps the
            // form value in sync via the hidden register call below.
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Workspace
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{
                  px: 1.75, py: 1.5, borderRadius: 2,
                  border: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                  <BusinessOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Chip
                    label={urlSlug}
                    size="small"
                    sx={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: 12.5, fontWeight: 600,
                      height: 24,
                    }}
                  />
                </Stack>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={useDifferentWorkspace}
                  sx={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Use a different workspace
                </Link>
              </Stack>
              {/* Hidden register so react-hook-form still has the value
                  for validation + submit; effectiveSlug is mirrored here. */}
              <input type="hidden" {...register('org_slug')} />
            </Box>
          ) : (
            // Bare /login — workspace is resolved server-side from
            // the email. Field is intentionally hidden so the form is
            // just email + password. If the user's email belongs to
            // multiple workspaces, we render the picker below instead
            // of the form (see workspaceOptions branch).
            <input type="hidden" {...register('org_slug')} />
          )}

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Email address
            </Typography>
            <TextField
              fullWidth
              size="medium"
              type="email"
              placeholder="john@example.com"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              {...register('email')}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="medium"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              {...register('password')}
            />
          </Box>

          <Box sx={{ textAlign: 'right', pt: 1 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Forgot password?
            </Link>
          </Box>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isSubmitting}
            endIcon={!isSubmitting && <ArrowForwardOutlinedIcon />}
            sx={{ borderRadius: 999, py: 1.5 }}
          >
            {isSubmitting ? 'Signing in...' : 'Log In'}
          </Button>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pt: 0.5 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', color: 'text.disabled' }}>
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Stack>

          <GoogleSignInButton onCredential={onGoogleCredential} disabled={isSubmitting} />
        </Stack>
      </Box>
      )}
    </AuthShell>
  )
}

/**
 * Shown when the email-first login matches more than one workspace.
 * User clicks one → parent runs the slugged login with the stashed
 * credentials and finishes the session.
 */
function WorkspacePicker({ options, onPick, onCancel, finalizing, email, error }: {
  options: { slug: string; name: string }[]
  onPick: (slug: string) => void
  onCancel: () => void
  finalizing: boolean
  email: string
  error: string | null
}) {
  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>
          Your email <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{email}</Box> belongs
          to multiple workspaces. Pick the one you want to sign in to.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      )}

      <Stack spacing={1}>
        {options.map((w) => (
          <Box
            key={w.slug}
            component="button"
            type="button"
            disabled={finalizing}
            onClick={() => onPick(w.slug)}
            sx={{
              all: 'unset',
              cursor: finalizing ? 'default' : 'pointer',
              opacity: finalizing ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.75, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': finalizing ? {} : { borderColor: 'primary.main', bgcolor: (t) => `${t.palette.primary.main}08` },
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em' }}>
                {w.name}
              </Typography>
              <Typography sx={{
                fontSize: 12, color: 'text.secondary',
                fontFamily: 'ui-monospace, monospace',
              }}>
                {w.slug}
              </Typography>
            </Box>
            <ArrowForwardOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          </Box>
        ))}
      </Stack>

      <Box sx={{ pt: 0.5 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onCancel}
          disabled={finalizing}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          ← Use a different email
        </Link>
      </Box>
    </Stack>
  )
}

/**
 * Shown when a Google sign-in found no matching account anywhere —
 * the backend queued it for admin review (same self-service
 * request-invite queue the manual signup form feeds).
 */
function GooglePendingNotice({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{
        width: 64, height: 64, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => `${t.palette.primary.main}1A`,
      }}>
        <MarkEmailReadOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>
        Request received
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6, maxWidth: 380 }}>
        {message}
      </Typography>
      <Link component="button" type="button" underline="hover" onClick={onBack} sx={{ fontSize: 13, fontWeight: 600 }}>
        ← Back to sign in
      </Link>
    </Stack>
  )
}
